import React from 'react';
import { DollarSign, RotateCcw, AlertTriangle, MessageSquare, Info, ShieldCheck, Mail } from 'lucide-react';

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-bgmain pb-16 md:pb-24 pt-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Banner */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-primary text-white p-8 md:p-12 mb-8 shadow-warm grain">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full mb-3 border border-white/10">
              <DollarSign className="w-4 h-4 text-golden" />
              <span className="text-xs font-heading font-medium tracking-wide">LEGAL POLICY</span>
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-white">Return, Refund & Contact Policy</h1>
            <p className="text-white/80 font-body text-xs mt-3 uppercase tracking-wider">Effective Date: July 18, 2026</p>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-3xl border border-borderbeige shadow-card p-6 md:p-10 font-body text-textprimary leading-relaxed space-y-8 animate-fade-in">
          
          {/* Section 1: Return Policy */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">
                <RotateCcw className="w-4 h-4" />
              </span>
              Return Policy
            </h2>
            <div className="text-textsecondary text-sm space-y-2">
              <p>
                Due to the perishable nature of freshly prepared food, <strong>returns are not accepted</strong>.
              </p>
              <p>
                Once an order has been prepared or delivered, it cannot be returned.
              </p>
            </div>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 2: Refund Policy */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">
                <DollarSign className="w-4 h-4" />
              </span>
              Refund Policy
            </h2>
            <div className="text-textsecondary text-sm space-y-3">
              <p className="font-semibold text-textprimary">
                Pushpanjali follows a <span className="text-maroon">No Return & No Refund Policy</span>.
              </p>
              
              <div className="bg-bgmain p-5 rounded-2xl border border-borderbeige space-y-2">
                <h3 className="font-heading font-semibold text-textprimary text-sm">Refunds will generally not be provided for:</h3>
                <ul className="list-disc list-inside text-xs text-textsecondary space-y-1 pl-2">
                  <li>Change of mind</li>
                  <li>Taste preference</li>
                  <li>Delayed consumption</li>
                  <li>Incorrect order placed by the customer</li>
                  <li>Minor variations in appearance</li>
                  <li>Delivery delays beyond reasonable control</li>
                </ul>
              </div>

              <div className="bg-fresh/5 p-5 rounded-2xl border border-fresh/15 space-y-2">
                <h3 className="font-heading font-semibold text-fresh text-sm flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4" /> A refund or replacement may be considered only if:
                </h3>
                <ul className="list-disc list-inside text-xs text-textsecondary space-y-1 pl-2">
                  <li>Pushpanjali cancels the order before delivery.</li>
                  <li>The ordered item is unavailable and no suitable alternative is accepted.</li>
                  <li>Required under applicable consumer protection laws.</li>
                </ul>
              </div>

              <p className="text-xs text-textsecondary mt-3">
                Any approved refund will be processed to the original payment method within the applicable banking timelines.
              </p>
            </div>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 3: Complaints */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">
                <AlertTriangle className="w-4 h-4" />
              </span>
              Complaints
            </h2>
            <div className="text-textsecondary text-sm space-y-3">
              <p>
                If you experience any genuine issue with your order, please contact us as soon as possible.
              </p>
              <p className="bg-saffron/5 border border-saffron/20 p-4 rounded-xl text-xs text-textprimary">
                <strong>Important:</strong> Complaints should ideally be raised within <strong>30 minutes of delivery</strong>.
              </p>
              <div className="space-y-1.5 pl-4">
                <p className="text-xs text-textsecondary">Please include:</p>
                <ul className="list-disc list-inside text-xs text-textsecondary space-y-1">
                  <li>Order ID</li>
                  <li>Photos (if applicable)</li>
                  <li>Description of the issue</li>
                </ul>
              </div>
              <p className="text-xs text-textsecondary italic">
                We may request supporting information before investigating.
              </p>
            </div>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 4: Delivery Partner */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">
                <Info className="w-4 h-4" />
              </span>
              Delivery Partner
            </h2>
            <p className="text-textsecondary text-sm">
              Orders are delivered through independent third-party delivery personnel. Once the order leaves our kitchen, minor delays caused by traffic, weather, society access restrictions, or delivery partner availability may occur. We request your patience in such situations.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Business & Contact Section */}
          <div className="grid md:grid-cols-2 gap-6 pt-4">
            
            {/* Contact details */}
            <div className="bg-bgmain p-6 rounded-2xl border border-borderbeige space-y-4">
              <h3 className="font-heading font-bold text-lg text-textprimary">Contact Us</h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-maroon/5 flex items-center justify-center text-maroon shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase font-heading font-bold text-textsecondary tracking-wider">Email Support</div>
                    <a href="mailto:contact@pushpanjali.cloud" className="text-sm font-semibold text-maroon hover:underline">
                      contact@pushpanjali.cloud
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-fresh/10 flex items-center justify-center text-fresh shrink-0">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase font-heading font-bold text-textsecondary tracking-wider">WhatsApp Support</div>
                    <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-fresh hover:underline">
                      Chat on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Info details */}
            <div className="bg-bgmain p-6 rounded-2xl border border-borderbeige space-y-4">
              <h3 className="font-heading font-bold text-lg text-textprimary">Business Information</h3>
              <div className="text-xs text-textsecondary space-y-2">
                <div><span className="font-semibold text-textprimary">Brand Name:</span> Pushpanjali</div>
                <div><span className="font-semibold text-textprimary">Website:</span> <a href="https://pushpanjali.cloud" target="_blank" rel="noopener noreferrer" className="text-maroon hover:underline">https://pushpanjali.cloud</a></div>
                <div><span className="font-semibold text-textprimary">Business Type:</span> Cloud Kitchen</div>
                <div>
                  <span className="font-semibold text-textprimary">Current Service Area:</span> Selected societies in Noida including Amrapali Princely Estate, Amrapali Silicon City, Futech Gateway, Aditya Celebrity Homes, Sector 76 and nearby serviceable locations.
                </div>
                <div>
                  <span className="font-semibold text-textprimary">FSSAI Registration:</span> Will be displayed on the website and food packaging after registration.
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
